import React, { useMemo, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { CSVLink } from "react-csv";
import dayjs from "dayjs";

const toNumber = (v) => Number(v || 0);

const RevenueHistoryTable = ({
  rows = [],
  loading = false,
  viewType = "water",
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const recordsPage = 6;

  const normalizedRows = useMemo(() => {
    if (viewType === "slrm") {
      return (rows || []).map((r) => ({
        bill_no: r?.sb_id ?? "",
        bill_date: r?.s_bill_date ? dayjs(r.s_bill_date).format("DD-MMM-YYYY") : "-",
        family_head: r?.family_head ?? "-",
        usage_value: toNumber(r?.s_total_days),
        bill_amount: toNumber(r?.bill_amount),
        old_due: toNumber(r?.old_due),
        total_amount: toNumber(r?.total_bill),
      }));
    }

    return (rows || []).map((r) => ({
      bill_no: r?.wb_id ?? "",
      bill_date: r?.bill_date ? dayjs(r.bill_date).format("DD-MMM-YYYY") : "-",
      family_head: r?.family_head ?? "-",
      usage_value: toNumber(r?.total_units),
      bill_amount: toNumber(r?.water_total_amount || r?.total_amt),
      old_due: toNumber(r?.old_due),
      total_amount: toNumber(r?.total_amt),
    }));
  }, [rows, viewType]);

  const npage = Math.max(1, Math.ceil(normalizedRows.length / recordsPage));
  const lastIndex = currentPage * recordsPage;
  const firstIndex = lastIndex - recordsPage;
  const records = normalizedRows.slice(firstIndex, lastIndex);
  const number = [...Array(npage + 1).keys()].slice(1);

  function prePage() {
    if (currentPage !== 1) setCurrentPage((p) => p - 1);
  }
  function changeCPage(id) {
    setCurrentPage(id);
  }
  function nextPage() {
    if (currentPage !== npage) setCurrentPage((p) => p + 1);
  }

  useEffect(() => {
    if (currentPage > npage) setCurrentPage(npage);
  }, [npage, currentPage]);

  const headersTitle = useMemo(() => {
    return [
      { label: "Bill No", key: "bill_no" },
      { label: "Date", key: "bill_date" },
      { label: "Family Head", key: "family_head" },
      {
        label: viewType === "slrm" ? "Total Days" : "Units",
        key: "usage_value",
      },
      {
        label: viewType === "slrm" ? "SLRM Amount" : "Water Amount",
        key: "bill_amount",
      },
      { label: "Old Due", key: "old_due" },
      { label: "Total", key: "total_amount" },
    ];
  }, [viewType]);

  const csvlink = useMemo(
    () => ({
      headers: headersTitle,
      data: normalizedRows,
      filename: `${viewType}_bill_history_${dayjs().format("YYYYMMDD_HHmm")}.csv`,
    }),
    [headersTitle, normalizedRows, viewType]
  );

  return (
    <div className="card">
      <div className="card-body p-0">
        <div className="table-responsive active-projects style-1">
          <div className="tbl-caption">
            <h4 className="heading mb-0">
              {viewType === "slrm" ? "SLRM Bill History" : "Water Bill History"}
            </h4>

            <div>
              <CSVLink {...csvlink} className="btn btn-primary light btn-sm me-2">
                <i className="fa-solid fa-file-excel" /> Export Report
              </CSVLink>
            </div>
          </div>

          <div className="dataTables_wrapper no-footer">
            <table className="table mb-2 mb-sm-0">
              <thead>
                <tr>
                  <th>Bill No</th>
                  <th>Date</th>
                  <th>Family Head</th>
                  <th className="text-end">
                    {viewType === "slrm" ? "Days" : "Units"}
                  </th>
                  <th className="text-end">
                    {viewType === "slrm" ? "SLRM Amount" : "Water Amount"}
                  </th>
                  <th className="text-end">Old Due</th>
                  <th className="text-end">Total</th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={7} className="text-center py-4">
                      Loading...
                    </td>
                  </tr>
                ) : records.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center py-4">
                      No bills found
                    </td>
                  </tr>
                ) : (
                  records.map((r, ind) => (
                    <tr key={ind}>
                      <td>
                        <b>#{r.bill_no}</b>
                      </td>
                      <td>{r.bill_date}</td>
                      <td>{r.family_head}</td>
                      <td className="text-end">{r.usage_value}</td>
                      <td className="text-end">₹{r.bill_amount}</td>
                      <td className="text-end">₹{r.old_due}</td>
                      <td className="text-end">
                        <span className="fw-bold">₹{r.total_amount}</span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>

            <div className="d-sm-flex text-center justify-content-between align-items-center px-3 pb-3">
              <div className="dataTables_info">
                {normalizedRows.length === 0 ? (
                  <>Showing 0 entries</>
                ) : (
                  <>
                    Showing {firstIndex + 1} to{" "}
                    {Math.min(lastIndex, normalizedRows.length)} of {normalizedRows.length} entries
                  </>
                )}
              </div>

              <div className="dataTables_paginate paging_simple_numbers justify-content-center">
                <Link
                  className={`paginate_button previous ${currentPage === 1 ? "disabled" : ""}`}
                  to="#"
                  onClick={(e) => {
                    e.preventDefault();
                    prePage();
                  }}
                >
                  <i className="fa-solid fa-angle-left" />
                </Link>

                <span>
                  {number.map((n) => (
                    <Link
                      key={n}
                      className={`paginate_button ${currentPage === n ? "current" : ""}`}
                      to="#"
                      onClick={(e) => {
                        e.preventDefault();
                        changeCPage(n);
                      }}
                    >
                      {n}
                    </Link>
                  ))}
                </span>

                <Link
                  className={`paginate_button next ${currentPage === npage ? "disabled" : ""}`}
                  to="#"
                  onClick={(e) => {
                    e.preventDefault();
                    nextPage();
                  }}
                >
                  <i className="fa-solid fa-angle-right" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RevenueHistoryTable;